/* eslint-disable max-lines */
import {
  ADDRESS,
  DIGITAL_LOCATION,
  excludePincodeCountries,
} from "../../components/organisms/daSectionTiles/addressForm/receiverAddress/constant";
import { ADD_ADDRESS_MAP, THIS_IS_REQUIRED } from "../../constants/pageConstants/deliveryAddressPageConstant";
import { checkServiceAvailabilityForPincode } from "../../api/deliveryAddressApi";
import { fetchPincodeList, sanitizePincodeList } from "../../api/pincodeLocationSuggestion";
import {
  buildServiceabilityPayload,
  buildOutOfStockByCartItemIndex,
  evaluateServiceabilityResponse,
  groupOrderListByServiceabilityPinCode,
  hasCheckoutGiftItemSavedAddress,
  isDomesticGiftItem,
  isServiceabilityItemFullyUnavailable,
  resolveCheckoutServiceabilityPinCode,
  resolveServiceabilityPinCodeForGiftItem,
  resolveUnifiedDomesticPincodeFromFirstItem,
} from "./serviceabilityForPincode";
import { formatCheckoutDeliveryDateLabel } from "./deliveryDateAndTimeSlot";
import CookieUtil from "../storage/cookieUtil";
import { LAST_VISITED_PLACE_TYPES } from "../../constants/storage";
import { execDataLayer, getCachedData } from "../cache-layer/cacheLayer";
import { endpointConst } from "../cache-layer/cacheLayerConstants";
import { fetchAccuracyWeightage } from "../../api/common";

/**
 * Accuracy weightage level ("high" | "medium" | "low") of Google place types, from the cached accuracy
 * weightage. The most accurate level among the types wins.
 * @param {string[]} types - Google place types.
 * @returns {string} level, or "" when the types are unknown or the weightage is not cached.
 */
export const getAccuracyLevelForTypes = (types = []) => {
  const placeTypes = Array.isArray(types) ? types.filter(Boolean) : [];
  const weightage = getCachedData(endpointConst.accuracyWeightage);
  if (!placeTypes.length || !weightage || typeof weightage !== "object") return "";
  const levels = placeTypes.map((type) => String(weightage[type] || "").toLowerCase());
  return ["high", "medium", "low"].find((level) => levels.includes(level)) || "";
};

/**
 * Serviceability location of a selected / saved address: its lat / long and accuracy level (a level, or a Google
 * type mapped to one), flagged so the APIs send it as it is instead of the latest gate / form location.
 * @param {{ lat?: number|string, long?: number|string, accuracyType?: string }} [address]
 * @returns {Object|null} { lat, long, accuracyType?, isSelectedAddressLocation } or null without coordinates.
 */
export const getAddressServiceabilityLocation = ({ lat, long, accuracyType } = {}) => {
  if (!Number(lat) || !Number(long)) return null;
  const accuracy = String(accuracyType || "").trim();
  const level = ["high", "medium", "low"].includes(accuracy.toLowerCase())
    ? accuracy.toLowerCase()
    : getAccuracyLevelForTypes([accuracy]);
  return {
    lat: Number(lat),
    long: Number(long),
    ...(level ? { accuracyType: level } : {}),
    isSelectedAddressLocation: true,
  };
};

// Google adds "geocode" to almost every prediction. It is medium, so it must not raise a low place into the
// suggestions (same as the map search's getSuggestionAreaAccuracy).
const GENERIC_SUGGESTION_TYPE = "geocode";

/**
 * Map address form suggestions: high and medium accuracy weightage only (same as the map search), loading the
 * weightage first when it is not cached; unfiltered when the weightage API is unavailable.
 * @param {Array} predictions - Google Places Autocomplete predictions.
 * @returns {Promise<Array>}
 */
export const filterSuggestionsByAccuracy = async (predictions) => {
  const list = Array.isArray(predictions) ? predictions : [];
  if (!getCachedData(endpointConst.accuracyWeightage)) {
    await execDataLayer(fetchAccuracyWeightage, endpointConst.accuracyWeightage);
  }
  if (!getCachedData(endpointConst.accuracyWeightage)) return list;
  return list.filter((prediction) => {
    const types = Array.isArray(prediction?.types) ? prediction.types : [];
    const specificTypes = types.filter((type) => type !== GENERIC_SUGGESTION_TYPE);
    return ["high", "medium"].includes(getAccuracyLevelForTypes(specificTypes.length ? specificTypes : types));
  });
};

/**
 * Accuracy weightage level ("high" | "medium" | "low") of the last selected location, from its Google
 * place types and the cached accuracy weightage. The most accurate level among the types wins.
 * @returns {string} level, or "" when it does not exist (pincode selection, unknown types, weightage not cached).
 */
export const getLastVisitedAccuracyLevel = () =>
  getAccuracyLevelForTypes(String(CookieUtil.getCookie(LAST_VISITED_PLACE_TYPES) || "").split(","));

export const LOW_ACCURACY_LEVEL = "low";

/**
 * "Enter the exact area" nudge: whether Google place types are low accuracy (generic "geocode" ignored, as in
 * the suggestion filter).
 * @param {Array<string>} types
 * @returns {boolean}
 */
export const isLowAccuracyPlaceTypes = (types = []) => {
  const placeTypes = Array.isArray(types) ? types.filter(Boolean) : [];
  const specificTypes = placeTypes.filter((type) => type !== GENERIC_SUGGESTION_TYPE);
  return getAccuracyLevelForTypes(specificTypes.length ? specificTypes : placeTypes) === LOW_ACCURACY_LEVEL;
};

/**
 * "Enter the exact area" nudge: the last location is a low accuracy Google place (never for a pincode).
 * @returns {Promise<boolean>}
 */
export const isLowAccuracyPlaceSelected = async () => {
  const placeTypes = String(CookieUtil.getCookie(LAST_VISITED_PLACE_TYPES) || "")
    .split(",")
    .filter(Boolean);
  if (!placeTypes.length) return false;
  if (!getCachedData(endpointConst.accuracyWeightage)) {
    await execDataLayer(fetchAccuracyWeightage, endpointConst.accuracyWeightage);
  }
  return isLowAccuracyPlaceTypes(placeTypes);
};

/**
 * Cart item delivery location (cart API shippingDetails) used for the map address form, or null when the cart
 * has none. accuracyType "null"/empty is treated as missing.
 * @param {Object} shippingDetails - orderList[i].giftItem.shippingDetails.
 * @returns {{ locality: string, accuracyType: string, lat: number, lng: number }|null}
 */
export const getCartShippingLocation = (shippingDetails) => {
  const locality = String(shippingDetails?.deliveryLocation || "").trim();
  if (!locality) return null;
  const accuracyType = String(shippingDetails?.accuracyType || "").trim();
  return {
    locality,
    accuracyType: accuracyType && accuracyType !== "null" ? accuracyType : "",
    lat: Number(shippingDetails?.lat) || 0,
    lng: Number(shippingDetails?.lng) || 0,
  };
};

/**
 * Google Autocomplete restriction box (LatLngBoundsLiteral) of `radiusKm` around a point, so place suggestions
 * stay near the delivery location.
 * @param {number} lat
 * @param {number} lng
 * @param {number} radiusKm - From web feature controls (mapAddressOptions.pincodeRadiusInKM).
 * @returns {{ north: number, south: number, east: number, west: number }|null} null when any value is missing.
 */
export const getRadiusBounds = (lat, lng, radiusKm) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  const radius = Number(radiusKm);
  if (!latitude || !longitude || !(radius > 0)) return null;
  const latDelta = radius / 111;
  const lngDelta = radius / (111 * Math.max(Math.cos((latitude * Math.PI) / 180), 0.01));
  return {
    north: latitude + latDelta,
    south: latitude - latDelta,
    east: longitude + lngDelta,
    west: longitude - lngDelta,
  };
};

/**
 * Accuracy weightage level of a cart delivery location (from its accuracyType), loading the weightage if needed.
 * @param {Object} shippingDetails - orderList[i].giftItem.shippingDetails.
 * @returns {Promise<string>} "high" | "medium" | "low", or "" when there is no accuracyType.
 */
export const resolveCartAccuracyLevel = async (shippingDetails) => {
  const accuracyType = getCartShippingLocation(shippingDetails)?.accuracyType;
  if (!accuracyType) return "";
  if (!getCachedData(endpointConst.accuracyWeightage)) {
    await execDataLayer(fetchAccuracyWeightage, endpointConst.accuracyWeightage);
  }
  return getAccuracyLevelForTypes([accuracyType]);
};

/**
 * Same as getLastVisitedAccuracyLevel, but loads the accuracy weightage first when it is not cached yet.
 * @returns {Promise<string>} level, or "" when it does not exist.
 */
export const resolveLastVisitedAccuracyLevel = async () => {
  if (CookieUtil.getCookie(LAST_VISITED_PLACE_TYPES) && !getCachedData(endpointConst.accuracyWeightage)) {
    await execDataLayer(fetchAccuracyWeightage, endpointConst.accuracyWeightage);
  }
  return getLastVisitedAccuracyLevel();
};

/**
 * Returns saved occasion for digital-location checkout items.
 * @param {Object} [giftItem]
 * @returns {string}
 */
const getDigitalGiftItemOccasion = (giftItem) => giftItem?.occasion || giftItem?.messageOnCard?.occasion || "";

const CHECKOUT_ADDRESS_FIELD_MAP = {
  [ADDRESS.TO_NAME]: "postalDtlsOfContactMechIdToName",
  [ADDRESS.MOBILE_NUMBER]: "postalDtlsOfContactMechIdContactNumber",
  [ADDRESS.EMAIL]: "postalDtlsOfContactMechIdEmail",
  [ADDRESS.ADDRESS_1]: "postalDtlsOfContactMechIdAddress1",
  [ADDRESS.ADDRESS_2]: "postalDtlsOfContactMechIdAddress2",
};

/** Countries where receiver email is mandatory (matched by catalogId or countryId). */
const EMAIL_REQUIRED_COUNTRIES = [
  { catalogId: "australia", countryId: "AUS" },
  { catalogId: "germany", countryId: "DEU" },
  { catalogId: "austria", countryId: "AUT" },
  { catalogId: "netherlands", countryId: "NLD" },
  { catalogId: "belgium", countryId: "BEL" },
];

/**
 * Whether receiver email is mandatory for the given catalog/country.
 * @param {Object} [params]
 * @param {string} [params.catalogId]
 * @param {string} [params.countryId]
 * @returns {boolean}
 */
export const isReceiverEmailRequired = ({ catalogId = "", countryId = "" } = {}) => {
  const normalizedCatalogId = String(catalogId || "").toLowerCase();
  const normalizedCountryId = String(countryId || "").toUpperCase();

  return EMAIL_REQUIRED_COUNTRIES.some(
    (country) =>
      (normalizedCatalogId && country.catalogId === normalizedCatalogId) ||
      (normalizedCountryId && country.countryId === normalizedCountryId),
  );
};

/**
 * @param {*} value
 * @returns {boolean}
 */
const isFieldValueEmpty = (value) =>
  value === undefined || value === null || String(value).trim() === "" || value === "_NA_";

/**
 * Resolves a form field from local address state or cart-selected address details.
 * @param {Object} item
 * @param {string} field
 * @param {Object} giftItem
 * @returns {string}
 */
const resolveAddressFieldValue = (item, field, giftItem) => {
  if (!isFieldValueEmpty(item?.[field])) {
    return String(item[field]).trim();
  }

  const selectedAddressKey = CHECKOUT_ADDRESS_FIELD_MAP[field];
  const selectedValue = selectedAddressKey ? giftItem?.selectedAddressDetails?.[selectedAddressKey] : "";

  if (isFieldValueEmpty(selectedValue)) {
    return "";
  }

  return String(selectedValue).trim();
};

/**
 * @param {Object} item
 * @param {string} field
 * @param {Object} giftItem
 * @returns {boolean}
 */
const isAddressFieldInvalid = (item, field, giftItem) => {
  const fieldValue = resolveAddressFieldValue(item, field, giftItem);
  const fieldErrorKey = `${field}Error`;
  const fieldErrorMsgKey = `${field}ErrorMsg`;

  if (isFieldValueEmpty(fieldValue)) {
    return true;
  }

  const isPresenceOnlyError = [THIS_IS_REQUIRED].includes(item[fieldErrorMsgKey]);
  return Boolean(item?.[fieldErrorKey] && item?.[fieldErrorMsgKey] && !isPresenceOnlyError);
};

/**
 * @param {Array} orderList
 * @returns {boolean}
 */
const hasDigitalLocationProductInCart = (orderList = []) =>
  orderList.some(({ giftItem }) => giftItem?.addressPurposeType === DIGITAL_LOCATION);

/**
 * Validates delivery address form and prepares payload for submission
 *
 * This function performs the following operations:
 * 1. Validates required fields for each address in the address list
 * 2. Handles special cases for digital locations and German addresses
 * 3. Prepares the payload for cart data update if validation passes
 * 4. Returns validation status and updated address list
 *
 * @param {Object} params - Parameters object
 * @param {Array<Object>} params.allAddressList - List of all addresses to validate
 * @param {Object} params.updateSenderData - Sender details containing sender information
 * @param {Object} params.completeCartDetails - Complete cart details including order list
 * @param {boolean} params.isMobile - Whether the view is mobile
 *
 * @returns {Object} Result object containing:
 * @returns {boolean} result.isValid - Whether the form validation passed
 * @returns {string} [result.payload] - Form data payload for cart update (only if isValid is true)
 * @returns {Array<Object>} result.updatedAddressList - Updated address list with validation states
 * @returns {number|null} result.errorindex - Index of the first address with validation error, null if no errors
 */
export const validateAndPrepareDeliveryAddress = ({ allAddressList, updateSenderData, completeCartDetails }) => {
  let errorindex = null;
  let isFormValid = true;
  let isFormSaved = true;
  let isSenderValid = true;
  let isMandatoryEmailMissing = false;
  let mandatoryEmailContactMechId = "";
  const updatedAddressList = [...allAddressList];
  const orderList = completeCartDetails?.orderList || [];
  const hasDigitalProductInCart = hasDigitalLocationProductInCart(orderList);
  const primaryGiftItem = orderList[0]?.giftItem;
  const primaryOccasion = getDigitalGiftItemOccasion(primaryGiftItem);
  const PRIMARY_INDEX = 0;

  if (hasDigitalProductInCart) {
    if (!primaryOccasion) {
      if (updatedAddressList[PRIMARY_INDEX]) {
        updatedAddressList[PRIMARY_INDEX].occasionError = true;
        updatedAddressList[PRIMARY_INDEX].occasionErrorAt = Date.now();
      }
      isFormValid = false;
      errorindex = PRIMARY_INDEX;
    } else if (updatedAddressList[PRIMARY_INDEX]) {
      updatedAddressList[PRIMARY_INDEX].occasionError = false;
      updatedAddressList[PRIMARY_INDEX].occasionErrorAt = undefined;
    }
  } else {
    updatedAddressList.forEach((_, index) => {
      if (updatedAddressList[index]) {
        updatedAddressList[index].occasionError = false;
        updatedAddressList[index].occasionErrorAt = undefined;
      }
    });
  }

  /**
   * Marks a field invalid on the address list item and records the first error index.
   * @param {number} index
   * @param {string} field
   * @param {Object} item
   * @param {Object} giftItem
   * @param {string} [requiredErrorMsg]
   */
  const markAddressFieldError = (index, field, item, giftItem, requiredErrorMsg = THIS_IS_REQUIRED) => {
    const fieldValue = resolveAddressFieldValue(item, field, giftItem);
    if (updatedAddressList[index]) {
      updatedAddressList[index][`${field}Error`] = true;
      updatedAddressList[index][`${field}ErrorMsg`] = isFieldValueEmpty(fieldValue)
        ? requiredErrorMsg
        : item[`${field}ErrorMsg`] || requiredErrorMsg;
      updatedAddressList[index].formError = true;
    }
    isFormValid = false;
    if (errorindex === null) {
      errorindex = index;
    }
  };

  updatedAddressList.forEach((item, index) => {
    const giftItem = completeCartDetails?.orderList[index]?.giftItem;
    const addressPurposeType = item?.addressPurposeType || giftItem?.addressPurposeType;
    const isDigitalLocation = addressPurposeType === DIGITAL_LOCATION;
    const isEmailMandatory = isReceiverEmailRequired({
      catalogId: giftItem?.productCatalogId,
      countryId: item?.countryGeoId || giftItem?.countryGeoId || giftItem?.selectedAddressDetails?.countryGeoId,
    });

    if (item?.isAddressAvailable) {
      const shouldValidateContactFields = !isDigitalLocation || Boolean(primaryOccasion);

      if (!shouldValidateContactFields) {
        return;
      }

      let requiredFields = [ADDRESS.TO_NAME, ADDRESS.MOBILE_NUMBER];
      if (!isDigitalLocation) {
        const countryGeoId =
          item?.countryGeoId || giftItem?.countryGeoId || giftItem?.selectedAddressDetails?.countryGeoId;
        requiredFields = [...requiredFields, ADDRESS.ADDRESS_1];
        if (!excludePincodeCountries.includes(countryGeoId)) {
          requiredFields = [...requiredFields, ADDRESS.ADDRESS_2];
        }
        if (item?.countryGeoId === "DEU") {
          requiredFields = [...requiredFields, ADDRESS.DOORBELL];
        }
      }
      if (isEmailMandatory) {
        requiredFields = [...requiredFields, ADDRESS.EMAIL];
      }
      requiredFields.forEach((field) => {
        if (isAddressFieldInvalid(item, field, giftItem)) {
          const requiredErrorMsg = THIS_IS_REQUIRED;
          markAddressFieldError(index, field, item, giftItem, requiredErrorMsg);
          if (field === ADDRESS.EMAIL && isEmailMandatory) {
            isMandatoryEmailMissing = true;
            mandatoryEmailContactMechId =
              giftItem?.selectedAddressDetails?.selectedContactMechId || item?.selectedContactMechId || "";
          }
        } else if (updatedAddressList[index]) {
          updatedAddressList[index][`${field}Error`] = false;
          updatedAddressList[index][`${field}ErrorMsg`] = "";
        }
      });
    } else if (isEmailMandatory && hasCheckoutGiftItemSavedAddress(giftItem)) {
      // Saved-address path sets isAddressAvailable=false; still block proceed when email is missing
      if (isAddressFieldInvalid(item, ADDRESS.EMAIL, giftItem)) {
        markAddressFieldError(index, ADDRESS.EMAIL, item, giftItem, THIS_IS_REQUIRED);
        isMandatoryEmailMissing = true;
        mandatoryEmailContactMechId = giftItem?.selectedAddressDetails?.selectedContactMechId || "";
      } else if (updatedAddressList[index]) {
        updatedAddressList[index][`${ADDRESS.EMAIL}Error`] = false;
        updatedAddressList[index][`${ADDRESS.EMAIL}ErrorMsg`] = "";
      }
    }
  });

  if (isFormValid) {
    const hasUnsavedAddressForm = updatedAddressList.some((item, index) => {
      const orderGiftItem = completeCartDetails?.orderList[index]?.giftItem;
      return Boolean(item?.isAddressAvailable) && !hasCheckoutGiftItemSavedAddress(orderGiftItem);
    });

    if (hasUnsavedAddressForm) {
      isFormSaved = false;
      updatedAddressList.forEach((item, index) => {
        const orderGiftItem = completeCartDetails?.orderList[index]?.giftItem;
        if (item?.isAddressAvailable && !hasCheckoutGiftItemSavedAddress(orderGiftItem)) {
          if (errorindex === null) {
            errorindex = index;
          }
          updatedAddressList[index].isFormError = true;
        }
      });
    }

    if (isFormSaved) {
      const { primaryContactNumber, senderCity, senderName } = updateSenderData;
      if (primaryContactNumber && senderCity && senderName) {
        isSenderValid = true;
      } else {
        isSenderValid = false;
      }
      if (isSenderValid) {
        const payload = {
          bill_party_id: updateSenderData?.senderId,
          checkoutpage: "deliveryaddress",
          makeProfileCity: "N",
          sCity: updateSenderData?.senderCity,
          sCountryCode: updateSenderData?.primaryCountryCode,
          sTel: updateSenderData?.primaryContactNumber,
          salutation: "",
          senderEmail: updateSenderData?.emailAddress,
          sname: updateSenderData?.senderName,
        };

        completeCartDetails?.orderList?.forEach((value) => {
          const {
            mainproduct: { personalizedImage = "", personalizedText = "" } = {},
            cartItemIndex,
            selectedAddressDetails: { selectedContactMechId },
            messageOnCake,
            specialInstructions,
          } = value.giftItem;
          payload[`cakeMsg_${cartItemIndex}`] = messageOnCake?.itemMessage;
          payload[`personalizedImage_${cartItemIndex}`] = personalizedImage || "";
          payload[`personalizedText_${cartItemIndex}`] = personalizedText || "";
          payload[`selectedContactMechId_${cartItemIndex}`] =
            updatedAddressList[cartItemIndex]?.selectedAddressContactMechId || selectedContactMechId;
          payload[`specialinstr_${cartItemIndex}`] = specialInstructions || "";
        });

        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => formData.append(key, value));

        return {
          isValid: true,
          payload: new URLSearchParams(formData).toString(),
          updatedAddressList,
          errorindex,
          isSenderValid,
        };
      }
    }
  }

  return {
    isValid: false,
    isSenderValid: isFormValid && isFormSaved ? isSenderValid : true,
    updatedAddressList,
    errorindex,
    isMandatoryEmailMissing,
    mandatoryEmailContactMechId,
  };
};

/**
 * This function gives the maxlength for pincode according to country
 *
 * @param {string} countryGeoId counrty
 * @returns {number} returns the maxlength.
 */
export const getMaxLengthForZipcode = (countryGeoId) => {
  let maxLength = 10;
  switch (countryGeoId) {
    case "USA":
      maxLength = 5;
      break;
    case "GBR":
      maxLength = 8;
      break;
    case "CAN":
      maxLength = 7;
      break;
    case "IRL":
      maxLength = 7;
      break;

    default:
      break;
  }
  return maxLength;
};

/**
 * Finds country metadata from catalog id or country geo id.
 *
 * @param {Array} countryListData
 * @param {Object} params
 * @returns {Object|null}
 */
export const findCountryMeta = (countryListData = [], { catalogId = "", countryGeoId = "" } = {}) => {
  if (!Array.isArray(countryListData)) {
    return null;
  }

  const normalizedCatalogId = catalogId?.toLowerCase?.() || "";

  if (normalizedCatalogId) {
    const catalogMatch = countryListData.find(
      (country) =>
        country?.catelogID?.toLowerCase?.() === normalizedCatalogId ||
        country?.catalogId?.toLowerCase?.() === normalizedCatalogId,
    );

    if (catalogMatch) {
      return catalogMatch;
    }
  }

  if (countryGeoId) {
    return countryListData.find((country) => country?.countryId === countryGeoId) || null;
  }

  return null;
};

/**
 * Resolves receiver teleCode for checkout address forms.
 *
 * @param {Object} params
 * @returns {string}
 */
export const resolveAddressTeleCode = ({
  addressDetails = {},
  visitedCountry = {},
  countryListData = [],
  catalogId = "",
} = {}) => {
  const explicitTeleCode = addressDetails?.teleCode || addressDetails?.[ADDRESS.TELE_CODE];
  const countryGeoId = addressDetails?.countryGeoId;
  const normalizedCatalogId = catalogId?.toLowerCase?.() || "";

  if (countryGeoId === "IND" || (!countryGeoId && normalizedCatalogId === "india")) {
    return explicitTeleCode || "91";
  }

  if (explicitTeleCode && explicitTeleCode !== "91") {
    return explicitTeleCode;
  }

  const visitedCatalog = (visitedCountry?.catelogID || visitedCountry?.catalogId || "").toLowerCase();

  if (
    visitedCountry?.countryCode &&
    (!normalizedCatalogId || visitedCatalog === normalizedCatalogId || visitedCountry?.countryId === countryGeoId)
  ) {
    return visitedCountry.countryCode;
  }

  const countryMeta = findCountryMeta(countryListData, { catalogId, countryGeoId });

  if (countryMeta?.countryCode) {
    return countryMeta.countryCode;
  }

  return explicitTeleCode || "91";
};

/**
 * Builds a structured payload object for address information to be submitted with a cart item.
 * Handles both digital location and physical address types, formatting keys dynamically using the cart item index.
 *
 * @param {number|string} cartItemIndex - The index of the cart item, used to suffix payload keys uniquely.
 * @param {string} addressPurposeType - Type of address, e.g., "DIGITAL_LOCATION" or other physical address type.
 * @param {Object} addressObj - An object containing address data. It may include fields like ADDRESS_1, CITY, EMAIL, etc.
 * @returns {Object} A payload object formatted with keys suffixed by the cart item index, ready to be sent to backend services.
 *
 * @example
 * const payload = buildAddressPayload(0, "DIGITAL_LOCATION", addressData);
 */
export const buildAddressPayload = (cartItemIndex, addressPurposeType, addressObj) => {
  const addr = addressObj || {};
  /**
   * Returns the input value if it's neither null, undefined, nor an empty string.
   * Otherwise, returns an empty string.
   *
   * @param {*} value - The value to check.
   * @returns {string} The original value if valid; otherwise, an empty string.
   */
  const checkEmpty = (value) => {
    return value !== null && value !== undefined && value !== "" ? value : "";
  };
  /**
   * Safely retrieves and sanitizes a value from the address object.
   * Falls back to an empty string if the value is null/undefined.
   * @param {string} key - The key to retrieve from the address object.
   * @returns {string} The sanitized value or an empty string.
   */
  const get = (key) => checkEmpty(addr[key]) || "";

  const basePayload = {
    [`addressId_${cartItemIndex}`]: "",
    [`rCountryCode_${cartItemIndex}`]: get(ADDRESS.TELE_CODE),
    [`aRCountryCode_${cartItemIndex}`]: get(ADDRESS.ALT_RECP_TELE_CODE),
    [`countryGeoId_${cartItemIndex}`]: get(ADDRESS.COUNTRY_GEO_ID),
    [`postalcode_${cartItemIndex}`]:
      addressPurposeType === DIGITAL_LOCATION ? get(ADDRESS.POSTAL_CODE) || "" : get(ADDRESS.POSTAL_CODE),
    [`salutation_${cartItemIndex}`]: "",
    [`rname_${cartItemIndex}`]: get(ADDRESS.TO_NAME),
    [`selectedContactMechId_${cartItemIndex}`]: addr.selectedContactMechId || "",
    [`recipientEmail_${cartItemIndex}`]: get(ADDRESS.EMAIL),
    [`rTel_${cartItemIndex}`]: get(ADDRESS.MOBILE_NUMBER),
    [`aRTel_${cartItemIndex}`]: addressPurposeType === DIGITAL_LOCATION ? "" : get(ADDRESS.ALT_RECP_NUMBER),
    [`city_${cartItemIndex}`]: get(ADDRESS.CITY),
    [`editContactMechId_${cartItemIndex}`]: "",
    [`stateGeoId_${cartItemIndex}`]: "_NA_",
    [`isOrderingForSelf_${cartItemIndex}`]: addr.isOrderingForSelf ? "Y" : "N",
    cartItemIndex,
  };

  if (addressPurposeType === DIGITAL_LOCATION) {
    return {
      ...basePayload,
      [`address_line_1_${cartItemIndex}`]: "_NA_",
      [`doorBellName_${cartItemIndex}`]: "",
    };
  } else {
    return {
      ...basePayload,
      [`address_line_1_${cartItemIndex}`]: get(ADDRESS.ADDRESS_1),
      [`address_line_2_${cartItemIndex}`]: get(ADDRESS.ADDRESS_LINE_2),
      [`address_line_3_${cartItemIndex}`]: get(ADDRESS.ADDRESS_3),
      [`address2_${cartItemIndex}`]: get(ADDRESS.ADDRESS_2),
      [`doorBellName_${cartItemIndex}`]: get(ADDRESS.DOORBELL),
      [`latitude_${cartItemIndex}`]: get(ADDRESS.LATITUDE),
      [`longitude_${cartItemIndex}`]: get(ADDRESS.LONGITUDE),
      [`landmark_${cartItemIndex}`]: get(ADDRESS.LANDMARK),
      // Address type selection is removed from the UI; always save as "other".
      [`addressType_${cartItemIndex}`]: "other",
    };
  }
};

/**
 * Gets the unique code for combo items.
 * @param {Array<Object>} orderList - The list of orders.
 * @returns {string} The unique code for combo items.
 */
export const getUniqueCodeForComboItems = (orderList) => {
  const uniqueCodeForComboItems = orderList?.reduce((acc, order, idx) => {
    const pid = order?.giftItem?.mainproduct?.productId;
    const countryGeoId = order?.giftItem?.countryGeoId;
    const orderSeq = order?.giftItem?.orderItemSeqId;
    acc += `${pid}-${countryGeoId}-${orderSeq}${idx < orderList?.length - 1 ? "-" : ""}`;
    return acc;
  }, "");
  return uniqueCodeForComboItems;
};

/**
 * Fetches serviceability items for cart order list and pincode.
 * Dedupes concurrent identical requests (same pin + productIds) so setAddress /
 * setCartItemValues refresh loops cannot fire per-product duplicate calls.
 *
 * @param {Array} orderList
 * @param {string} pinCode
 * @returns {Promise<Array|null>}
 */
let inFlightServiceabilityByKey = null;

/**
 * Fetches serviceability items for cart order list and pincode.
 * @param {Array} orderList
 * @param {string} pinCode
 * @returns {Promise<Array|null>}
 */
export const fetchCartServiceabilityItems = async (orderList = [], pinCode = "", location = null) => {
  const payload = { ...buildServiceabilityPayload(orderList, pinCode), ...(location || {}) };

  if (!pinCode || !payload.productIds.length) {
    return [];
  }

  const requestKey = `${pinCode}:${location?.lat || ""},${location?.long || ""}:${payload.productIds
    .map((item) => `${item.pid}:${item.selectedDate}`)
    .join("|")}`;

  if (inFlightServiceabilityByKey?.key === requestKey) {
    return inFlightServiceabilityByKey.promise;
  }

  const requestPromise = (async () => {
    const response = await checkServiceAvailabilityForPincode(payload);
    return Array.isArray(response?.data) ? response.data : null;
  })();

  inFlightServiceabilityByKey = { key: requestKey, promise: requestPromise };

  try {
    return await requestPromise;
  } finally {
    if (inFlightServiceabilityByKey?.key === requestKey) {
      inFlightServiceabilityByKey = null;
    }
  }
};

/**
 * Checks cart serviceability and returns evaluation status with raw items.
 *
 * @param {Array} orderList
 * @param {string} pinCode
 * @param {Object|null} [location] - Selected address location (getAddressServiceabilityLocation).
 * @returns {Promise<{ status: "error"|"warning"|"success", items: Array }|null>}
 */
export const checkCartServiceabilityStatus = async (orderList = [], pinCode = "", location = null) => {
  if (!pinCode) {
    return { status: "success", items: [] };
  }

  const payload = buildServiceabilityPayload(orderList, pinCode);
  if (!payload.productIds.length) {
    return { status: "success", items: [] };
  }

  const items = await fetchCartServiceabilityItems(orderList, pinCode, location);
  if (items === null) {
    return null;
  }

  return {
    status: evaluateServiceabilityResponse(items),
    items,
  };
};

/**
 * Maps serviceability response items that need delivery date rescheduling.
 *
 * @param {Array} serviceItems
 * @param {Array} orderList
 * @returns {Array<{ pid: string, earliestAvailableDate: string, cartItemIndex: number }>}
 */
export const getRescheduledDeliveryItems = (serviceItems = [], orderList = []) => {
  return serviceItems
    .filter((item) => item?.isDeliverable === false && item?.earliestAvailableDate)
    .flatMap((item) =>
      orderList
        .filter((order) => order?.giftItem?.mainproduct?.productId === item.pid)
        .map((order) => ({
          pid: item.pid,
          earliestAvailableDate: item.earliestAvailableDate,
          cartItemIndex: order?.giftItem?.cartItemIndex,
        }))
        .filter((entry) => entry.cartItemIndex !== undefined && entry.cartItemIndex !== null),
    );
};

/**
 * Runs checkout landing serviceability checks once per pincode group.
 *
 * @param {Array} orderList
 * @returns {Promise<{ outOfStockByCartItemIndex: Object, serviceItemsByPinCode: Object, serviceableOrderList: Array, rescheduledItems: Array }>}
 */
export const evaluateCheckoutLandingServiceability = async (orderList = []) => {
  const outOfStockByCartItemIndex = {};
  const serviceItemsByPinCode = {};
  const serviceableOrderList = [];
  const rescheduledItems = [];

  if (!orderList?.length) {
    return { outOfStockByCartItemIndex, serviceItemsByPinCode, serviceableOrderList, rescheduledItems };
  }

  const pinCodeGroups = groupOrderListByServiceabilityPinCode(orderList);
  const unifiedPincode = resolveUnifiedDomesticPincodeFromFirstItem(orderList);

  orderList.forEach((order) => {
    const giftItem = order?.giftItem;
    if (!resolveCheckoutServiceabilityPinCode(giftItem, giftItem?.productCatalogId, unifiedPincode)) {
      serviceableOrderList.push(order);
    }
  });

  if (!pinCodeGroups.size) {
    return {
      outOfStockByCartItemIndex,
      serviceItemsByPinCode,
      serviceableOrderList: orderList,
      rescheduledItems,
    };
  }

  for (const [pinCode, groupedOrders] of pinCodeGroups.entries()) {
    const serviceItems = await fetchCartServiceabilityItems(groupedOrders, pinCode);
    serviceItemsByPinCode[pinCode] = serviceItems || [];

    if (!serviceItems?.length) {
      serviceableOrderList.push(...groupedOrders);
      continue;
    }
    Object.assign(outOfStockByCartItemIndex, buildOutOfStockByCartItemIndex(serviceItems, groupedOrders));

    groupedOrders.forEach((order) => {
      const pid = order?.giftItem?.mainproduct?.productId;
      const serviceItem = serviceItems.find((item) => item.pid === pid);

      if (!serviceItem || !isServiceabilityItemFullyUnavailable(serviceItem)) {
        serviceableOrderList.push(order);
      }
    });

    rescheduledItems.push(...getRescheduledDeliveryItems(serviceItems, groupedOrders));
  }

  return { outOfStockByCartItemIndex, serviceItemsByPinCode, serviceableOrderList, rescheduledItems };
};

/**
 * Builds setCartItemValues payload for checkout delivery date update.
 * `cartItemIndex` may be a single index or a comma-joined list (same date/pincode).
 *
 * @param {{ cartItemIndex: number|string, deliveryDate: string, pincode: string }} params
 * @returns {string}
 */
export const buildCheckoutDeliveryDateUpdatePayload = ({ cartItemIndex, deliveryDate, pincode }) => {
  return new URLSearchParams({
    itemIndex: String(cartItemIndex),
    deliveryDate,
    pincode,
  }).toString();
};

/**
 * Groups rescheduled items by delivery date so one setCartItemValues covers many indexes.
 *
 * @param {Array<{ cartItemIndex: number|string, earliestAvailableDate: string }>} rescheduledItems
 * @param {string} pincode
 * @returns {Array<string>} form-urlencoded payloads
 */
export const buildBatchedCheckoutDeliveryDateUpdatePayloads = (rescheduledItems = [], pincode = "") => {
  if (!rescheduledItems.length || !pincode) {
    return [];
  }

  const indexesByDate = rescheduledItems.reduce((acc, item) => {
    const deliveryDate = item?.earliestAvailableDate;
    const cartItemIndex = item?.cartItemIndex;

    if (!deliveryDate || cartItemIndex === undefined || cartItemIndex === null) {
      return acc;
    }

    if (!acc[deliveryDate]) {
      acc[deliveryDate] = [];
    }

    acc[deliveryDate].push(cartItemIndex);
    return acc;
  }, {});

  return Object.entries(indexesByDate).map(([deliveryDate, cartItemIndexes]) =>
    buildCheckoutDeliveryDateUpdatePayload({
      cartItemIndex: [...new Set(cartItemIndexes)].join(","),
      deliveryDate,
      pincode,
    }),
  );
};

/**
 * Returns cart item indexes that are out of stock but not yet marked unserviceable on cart.
 *
 * @param {Record<number, boolean>} outOfStockByCartItemIndex
 * @param {Array} orderList
 * @returns {Array<number|string>}
 */
export const getUnserviceableCartItemIndexesToSync = (outOfStockByCartItemIndex = {}, orderList = []) => {
  return orderList
    .filter((order) => {
      const cartItemIndex = order?.giftItem?.cartItemIndex;
      const isOutOfStock = Boolean(outOfStockByCartItemIndex[cartItemIndex]);
      const isAlreadyMarkedUnserviceable = order?.giftItem?.isUnserviceable === "Y";

      return isOutOfStock && !isAlreadyMarkedUnserviceable;
    })
    .map((order) => order?.giftItem?.cartItemIndex)
    .filter((cartItemIndex) => cartItemIndex !== undefined && cartItemIndex !== null)
    .sort((firstIndex, secondIndex) => Number(firstIndex) - Number(secondIndex));
};

/**
 * Returns cart item indexes marked unserviceable on cart that are serviceable again
 * after a pincode change (e.g. previously OOS, now deliverable at the new pin).
 *
 * @param {Record<number, boolean>} outOfStockByCartItemIndex
 * @param {Array} orderList
 * @returns {Array<number|string>}
 */
export const getServiceableCartItemIndexesToSync = (outOfStockByCartItemIndex = {}, orderList = []) => {
  return orderList
    .filter((order) => {
      const giftItem = order?.giftItem;
      const cartItemIndex = giftItem?.cartItemIndex;
      const isOutOfStock = Boolean(outOfStockByCartItemIndex[cartItemIndex]);
      const isMarkedUnserviceable = giftItem?.isUnserviceable === "Y";
      const hasServiceabilityPin = Boolean(
        resolveServiceabilityPinCodeForGiftItem(giftItem, giftItem?.productCatalogId, orderList),
      );

      return isMarkedUnserviceable && !isOutOfStock && hasServiceabilityPin;
    })
    .map((order) => order?.giftItem?.cartItemIndex)
    .filter((cartItemIndex) => cartItemIndex !== undefined && cartItemIndex !== null)
    .sort((firstIndex, secondIndex) => Number(firstIndex) - Number(secondIndex));
};

/**
 * Builds form-urlencoded payload to update cart item unserviceable flags.
 *
 * @param {Array<number|string>} cartItemIndexes
 * @param {"Y"|"N"} [isUnserviceable="Y"]
 * @returns {string}
 */
export const buildUnserviceableCartItemsPayload = (cartItemIndexes = [], isUnserviceable = "Y") => {
  const formData = new FormData();
  formData.append("itemIndex", cartItemIndexes.join(","));
  formData.append("isUnserviceable", isUnserviceable);

  return new URLSearchParams(formData).toString();
};

export { hasCheckoutGiftItemSavedAddress } from "./serviceabilityForPincode";

/**
 * When the first cart item (latest added) has a saved address and other items do not,
 * returns payload parts for setAddressForItem-rj sync.
 *
 * @param {Array} orderList
 * @returns {{ missingCartItemIndexes: Array<number|string>, sourceCartItemIndex: number|string, contactMechId: string }|null}
 */
export const getMissingAddressCartItemIndexesToSync = (orderList = []) => {
  if (orderList.length <= 1) {
    return null;
  }

  const sourceGiftItem = orderList[0]?.giftItem;

  if (!hasCheckoutGiftItemSavedAddress(sourceGiftItem)) {
    return null;
  }

  const missingCartItemIndexes = orderList
    .slice(1)
    .filter((order) => order?.giftItem?.selectedAddressDetails?.postalDtlsOfContactMechIdToName === "")
    .map((order) => order?.giftItem?.cartItemIndex)
    .filter((cartItemIndex) => cartItemIndex !== undefined && cartItemIndex !== null)
    .sort((firstIndex, secondIndex) => Number(firstIndex) - Number(secondIndex));

  if (!missingCartItemIndexes.length) {
    return null;
  }

  return {
    missingCartItemIndexes,
    sourceCartItemIndex: sourceGiftItem.cartItemIndex,
    contactMechId: sourceGiftItem.selectedAddressDetails.selectedContactMechId,
  };
};

/**
 * When the first cart item has pincode only (no saved address), returns other domestic
 * items whose serviceability pin differs so they can be aligned to the latest pincode.
 *
 * @param {Array} orderList
 * @returns {{ pincode: string, cartItemIndexes: Array<number|string> }|null}
 */
export const getCartItemIndexesToSyncPincodeFromFirstItem = (orderList = []) => {
  if (orderList.length <= 1) {
    return null;
  }

  const sourceGiftItem = orderList[0]?.giftItem;

  if (!sourceGiftItem || hasCheckoutGiftItemSavedAddress(sourceGiftItem)) {
    return null;
  }

  const sourcePincode = sourceGiftItem?.mainproduct?.pincode || "";

  if (!sourcePincode) {
    return null;
  }

  const cartItemIndexes = orderList
    .slice(1)
    .filter((order) => isDomesticGiftItem(order?.giftItem))
    .filter((order) => {
      const giftItem = order?.giftItem;
      const mainPin = giftItem?.mainproduct?.pincode || "";
      const addressPin =
        giftItem?.selectedAddressDetails?.postalDtlsOfContactMechIdPostalCode ||
        giftItem?.selectedAddressDetails?.postalCode ||
        "";
      const resolvedPin = mainPin || addressPin;

      return resolvedPin !== sourcePincode;
    })
    .map((order) => order?.giftItem?.cartItemIndex)
    .filter((cartItemIndex) => cartItemIndex !== undefined && cartItemIndex !== null)
    .sort((firstIndex, secondIndex) => Number(firstIndex) - Number(secondIndex));

  if (!cartItemIndexes.length) {
    return null;
  }

  return {
    pincode: sourcePincode,
    cartItemIndexes,
  };
};

/**
 * When the first cart item has a saved address, returns other domestic items whose
 * product/address pin differs so they can be aligned via setCartItemValues after
 * setAddressForItem-rj (e.g. checkout saved-address confirm).
 *
 * @param {Array} orderList
 * @returns {{ pincode: string, cartItemIndexes: Array<number|string> }|null}
 */
export const getCartItemIndexesToSyncPincodeFromSavedAddress = (orderList = []) => {
  if (orderList.length <= 1) {
    return null;
  }

  const sourceGiftItem = orderList[0]?.giftItem;

  if (!sourceGiftItem || !hasCheckoutGiftItemSavedAddress(sourceGiftItem)) {
    return null;
  }

  const sourcePincode =
    sourceGiftItem?.selectedAddressDetails?.postalDtlsOfContactMechIdPostalCode ||
    sourceGiftItem?.selectedAddressDetails?.postalCode ||
    "";

  if (!sourcePincode) {
    return null;
  }

  const cartItemIndexes = orderList
    .slice(1)
    .filter((order) => isDomesticGiftItem(order?.giftItem))
    .filter((order) => {
      const giftItem = order?.giftItem;
      const mainPin = giftItem?.mainproduct?.pincode || "";
      const addressPin =
        giftItem?.selectedAddressDetails?.postalDtlsOfContactMechIdPostalCode ||
        giftItem?.selectedAddressDetails?.postalCode ||
        "";
      const resolvedPin = mainPin || addressPin;

      return resolvedPin !== sourcePincode;
    })
    .map((order) => order?.giftItem?.cartItemIndex)
    .filter((cartItemIndex) => cartItemIndex !== undefined && cartItemIndex !== null)
    .sort((firstIndex, secondIndex) => Number(firstIndex) - Number(secondIndex));

  if (!cartItemIndexes.length) {
    return null;
  }

  return {
    pincode: sourcePincode,
    cartItemIndexes,
  };
};

/**
 * Builds setCartItemValues payload to align cart items to the first item's pincode.
 *
 * @param {{ cartItemIndexes: Array<number|string>, pincode: string }} params
 * @returns {string}
 */
export const buildPincodeSyncForCartItemsPayload = ({ cartItemIndexes = [], pincode = "" }) => {
  if (!cartItemIndexes.length || !pincode) {
    return "";
  }

  return new URLSearchParams({
    itemIndex: cartItemIndexes.join(","),
    pincode,
  }).toString();
};

/**
 * Builds setAddressForItem-rj payload to copy a saved address to missing cart items.
 *
 * @param {{ missingCartItemIndexes: Array<number|string>, sourceCartItemIndex: number|string, contactMechId: string }} params
 * @returns {string}
 */
export const buildSetAddressForMissingItemsPayload = ({
  missingCartItemIndexes = [],
  contactMechId = "",
  sourceCartItemIndex = "",
}) => {
  const formData = new FormData();
  formData.append(
    "cartItemIndex",
    [sourceCartItemIndex !== "" ? sourceCartItemIndex : "", ...missingCartItemIndexes].join(","),
  );
  formData.append(`selectedContactMechId_0`, contactMechId);

  return new URLSearchParams(formData).toString();
};

/**
 * Builds nudge state map for rescheduled delivery items.
 *
 * @param {Array} rescheduledItems
 * @returns {Object}
 */
export const buildDeliveryDateNudgesState = (rescheduledItems = []) => {
  return rescheduledItems.reduce((acc, item) => {
    acc[item.cartItemIndex] = {
      formattedDate: formatCheckoutDeliveryDateLabel(item.earliestAvailableDate, true),
      isVisible: true,
      isHiding: false,
    };
    return acc;
  }, {});
};

/**
 * Checks whether any cart gift item has a time slot selected.
 *
 * @param {Array} orderList
 * @returns {{ isTimeSlotExists: boolean, shippingPrice: number|string|null }}
 */
export const getCheckoutTimeSlotSelectionFromOrderList = (orderList = []) => {
  const selectedOrder = orderList.find((order) => {
    const deliveryTimeSlotId = order?.giftItem?.shippingDetails?.deliveryTimeSlotId;
    return deliveryTimeSlotId !== undefined && deliveryTimeSlotId !== null && deliveryTimeSlotId !== "";
  });

  if (!selectedOrder) {
    return {
      isTimeSlotExists: false,
      shippingPrice: null,
    };
  }

  return {
    isTimeSlotExists: true,
    shippingPrice: selectedOrder?.giftItem?.shippingDetails?.shippingPrice ?? null,
  };
};

/**
 * Utility function to format address to "City, State, Country".
 *
 * Logic:
 * - Splits address string by commas.
 * - If total parts <= 3: returns the original address string as is.
 * - If total parts > 3: returns the last 3 parts (City, State, Country).
 *
 * @param {string} address - The raw address string.
 * @return {string} - Formatted address string.
 */
export const getCityStateCountry = (address) => {
  if (!address || typeof address !== "string") return "";

  const parts = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length <= 3) {
    return address.trim();
  }
  return parts.slice(-3).join(", ");
};

/**
 * Map form: swaps the generic required error for the field's own message (display only).
 * @param {boolean} isMapAddressForm
 * @param {string} field
 * @param {string} errorMsg
 * @returns {string}
 */
export const getMapFormErrorMsg = (isMapAddressForm, field, errorMsg) =>
  isMapAddressForm && errorMsg === THIS_IS_REQUIRED ? ADD_ADDRESS_MAP.REQUIRED_ERRORS[field] || errorMsg : errorMsg;

export const TYPED_LOCALITY_PLACE_ID = "typed-locality";

/**
 * Map form: suggestion row for the customer's own text when Google returns no suggestions.
 * @param {string} text
 * @returns {Object}
 */
export const getTypedLocalitySuggestion = (text) => ({
  place_id: TYPED_LOCALITY_PLACE_ID,
  description: text,
  structured_formatting: { main_text: text, secondary_text: "" },
  types: [],
});

/**
 * Map form: uses the customer's own text as the locality; the map location stays as it is.
 * @param {Function} setValue
 * @param {string} text
 */
export const applyTypedLocality = (setValue, text) => {
  setValue(ADDRESS.ADDRESS_2, text);
  setValue(ADDRESS.ADDRESS_LINE_2, text);
  setValue(ADDRESS.LOCALITY_TITLE, text);
  setValue(ADDRESS.LOCALITY_TYPES, []);
  setValue(ADDRESS.ADDRESS_2_ERROR, false);
  setValue(ADDRESS.ADDRESS_2_ERROR_MSG, "");
};

/**
 * Merges map-form visit facts used only by New Address Added.
 * `locationSource` counts as one location change. Other fields only fill in.
 * @param {Object} [current]
 * @param {{ locationSource?: string, source?: string, pincodeSource?: string, pincodeSuggestionShown?: boolean, pincodeMismatchWarningShown?: boolean }} [patch]
 * @returns {Object}
 */
export const mergeAddressLocationTracking = (current, patch = {}) => {
  const next = {
    source: "",
    changeCount: 0,
    pincodeSource: "",
    pincodeSuggestionShown: false,
    pincodeMismatchWarningShown: false,
    ...(current && typeof current === "object" ? current : {}),
  };
  if (patch.locationSource) {
    next.source = patch.locationSource;
    next.changeCount = Number(next.changeCount || 0) + 1;
  } else if (patch.source) {
    next.source = patch.source;
  }
  if (patch.pincodeSource) next.pincodeSource = patch.pincodeSource;
  if (patch.pincodeSuggestionShown) next.pincodeSuggestionShown = true;
  if (patch.pincodeMismatchWarningShown) next.pincodeMismatchWarningShown = true;
  if (patch.markLocationSelected) next.hasUserSelectedLocation = true;
  if ("selectedAccuracy" in patch) next.selectedAccuracy = String(patch.selectedAccuracy || "");
  if (patch.pincodeSuggestionReason) {
    next.pincodeSuggestionReason = patch.pincodeSuggestionReason;
    if (patch.suggestedPincode) next.pincodeSuggestionFor = String(patch.suggestedPincode);
  }
  if ("derivedPincode" in patch) next.derivedPincode = String(patch.derivedPincode || "");
  return next;
};

/**
 * Google geocode of an Indian pincode: "City, State" (empty when not found).
 * @param {string} pincode
 * @returns {Promise<string>}
 */
const geocodePincodeCity = (pincode) =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || !window.google?.maps?.Geocoder) {
      resolve("");
      return;
    }
    new window.google.maps.Geocoder().geocode(
      { componentRestrictions: { postalCode: pincode, country: "IN" } },
      (results, status) => {
        const components = status === "OK" ? results?.[0]?.address_components || [] : [];
        /** Long name of the first component of a type. */
        const getPart = (type) => components.find((part) => part.types?.includes(type))?.long_name || "";
        const city =
          getPart("locality") || getPart("administrative_area_level_3") || getPart("administrative_area_level_2");
        resolve([city, getPart("administrative_area_level_1")].filter(Boolean).join(", "));
      },
    );
  });

/**
 * City for a 6-digit pincode: the pincode list API first, then Google geocode.
 * @param {string} pincode
 * @returns {Promise<string>}
 */
export const resolvePincodeCity = async (pincode) => {
  const value = String(pincode || "").trim();
  if (!/^\d{6}$/.test(value)) return "";
  try {
    const match = sanitizePincodeList(await fetchPincodeList(value.slice(0, 2))).find(
      (item) => String(item?.pincode) === value,
    );
    if (match?.location) return String(match.location);
  } catch {
    // falls back to Google geocode
  }
  return geocodePincodeCity(value);
};
